import { z } from 'zod';
import { mediaIdSchema, mediaTypeSchema } from './content';
import type { ProviderId } from './provider';

export const rawStreamSchema = z.object({
  url: z.string().max(4000).optional(),
  ytId: z.string().max(50).optional(),
  infoHash: z
    .string()
    .regex(/^[a-fA-F0-9]{40}$/)
    .optional(),
  fileIdx: z.number().int().min(0).max(100000).optional(),
  externalUrl: z.string().max(4000).optional(),
  name: z.string().max(500).optional(),
  title: z.string().max(2000).optional(),
  description: z.string().max(2000).optional(),
  subtitles: z
    .array(
      z.object({
        id: z.string().max(200).optional(),
        url: z.string().max(4000),
        lang: z.string().max(20),
      }),
    )
    .max(100)
    .optional(),
  behaviorHints: z
    .object({
      notWebReady: z.boolean().optional(),
      bingeGroup: z.string().max(500).optional(),
      filename: z.string().max(500).optional(),
      videoSize: z.number().nonnegative().optional(),
      countryWhitelist: z.array(z.string().max(5)).max(300).optional(),
    })
    .loose()
    .optional(),
});
export type RawStream = z.infer<typeof rawStreamSchema>;

export type StreamKind = 'direct' | 'torrent' | 'external' | 'youtube';
export type Resolution = '8K' | '4K' | '1440p' | '1080p' | '720p' | '480p' | '360p' | 'SD';

export interface StreamQuality {
  resolution: Resolution | null;
  hdr: ('HDR' | 'HDR10+' | 'Dolby Vision')[];
  codec: 'HEVC' | 'H.264' | 'AV1' | null;
  audio: string[];
  estimated: boolean;
}

export interface NormalizedStream {
  id: string;
  addonId: string;
  addonName: string;
  kind: StreamKind;
  name: string;
  title: string;
  filename: string | null;
  sizeBytes: number | null;
  sizeEstimated: boolean;
  quality: StreamQuality;
  languages: string[];
  /** Torrent streams play through the account's debrid provider. */
  requiresProvider: 'debrid' | null;
  availability: 'playable' | 'needs-resolution' | 'needs-provider' | 'unsupported';
  unsupportedReason: string | null;
  notWebReady: boolean;
  hasSubtitles: boolean;
  cached: boolean | null;
}

export interface BestStreamResult extends ResolveSession {
  streamId: string;
  tried: number;
}

export const streamQuerySchema = z.object({
  type: mediaTypeSchema,
  id: mediaIdSchema,
  season: z.coerce.number().int().min(0).max(1000).optional(),
  episode: z.coerce.number().int().min(0).max(10000).optional(),
});

export const bestStreamSchema = streamQuerySchema.extend({
  exclude: z.array(z.string().max(100)).max(50).optional(),
});

export interface StreamListResponse {
  streams: NormalizedStream[];
  addonsQueried: number;
  addonErrors: { addonName: string; message: string }[];
  providerConnected: boolean;
  /** The connected debrid provider, if any. */
  provider?: ProviderId | null;
  noAddons: boolean;
  /** Addon ids in the profile's chosen order, for grouping the list. */
  addonOrder?: string[];
  stremioId: string | null;
  missingExternalId: boolean;
}

export const resolveStreamSchema = z.object({ streamId: z.string().min(8).max(100) });

export type ResolveStatus =
  'resolving' | 'waiting' | 'processing' | 'ready' | 'unavailable' | 'error';

export interface PlaybackSubtitle {
  id: string;
  lang: string;
  label: string;
  src: string;
}

export interface PlaybackSource {
  url: string;
  format: 'hls' | 'dash' | 'progressive';
  mimeType: string | null;
  filename: string | null;
  sizeBytes: number | null;
  subtitles: PlaybackSubtitle[];
  transcodeAvailable: boolean;
  isDemo: boolean;
  sessionId?: string;
  compatAvailable?: boolean;
  /** The Windows app: the same file through its own server, which reads ahead and survives drops. */
  localUrl?: string;
}

export interface MediaAudioTrack {
  index: number;
  codec: string;
  language: string | null;
  title: string | null;
  channels: string | null;
  isDefault: boolean;
  browserSafe: boolean;
}

export interface MediaVideoInfo {
  codec: string;
  width: number | null;
  height: number | null;
  tenBit: boolean;
  /** HDR transfer: PQ (HDR10, Dolby Vision) or HLG. */
  hdr: 'pq' | 'hlg' | null;
  /** Dolby Vision profile; 5 has no HDR10 base layer, so plain HEVC decoding shows wrong colours. */
  doviProfile: number | null;
}

/** What the player's converter (desktop app only) found out about a stream's file. */
export interface MediaInfo {
  /** The file can be repackaged for the player with its video untouched. */
  available: boolean;
  duration: number | null;
  videoCodec: string | null;
  /** MSE type of the repackaged file (video copied, audio AAC). */
  compatMime: string | null;
  audio: MediaAudioTrack[];
  needsAudioFix: boolean;
  /** ffmpeg's name for the container, e.g. matroska, mov, avi, mpegts. */
  container: string | null;
  video: MediaVideoInfo | null;
  /** MSE type when the video is re-encoded to H.264, for codecs this PC can't decode. */
  transcodeMime: string | null;
}

/** How the player is showing a file: as is, repackaged, or with its video re-encoded. */
export type PlaybackMode = 'direct' | 'remux' | 'transcode';

export const AUDIO_CODEC_LABELS: Record<string, string> = {
  aac: 'AAC',
  ac3: 'Dolby Digital',
  eac3: 'Dolby Digital Plus',
  truehd: 'Dolby TrueHD',
  dts: 'DTS',
  mp3: 'MP3',
  opus: 'Opus',
  vorbis: 'Vorbis',
  flac: 'FLAC',
};

export interface ResolveSession {
  id: string;
  status: ResolveStatus;
  progress: number | null;
  message: string | null;
  playback: PlaybackSource | null;
}

export const progressSchema = z.object({
  mediaId: mediaIdSchema,
  contentType: mediaTypeSchema,
  seasonNumber: z.number().int().min(0).max(1000).nullable().optional(),
  episodeNumber: z.number().int().min(0).max(10000).nullable().optional(),
  position: z.number().min(0).max(1e6),
  duration: z.number().min(0).max(1e6),
  /** False when this is the last save as the player closes. Older apps leave it out. */
  live: z.boolean().optional(),
  snapshot: z
    .object({
      title: z.string().max(300),
      posterUrl: z.string().max(2000).nullable(),
      backdropUrl: z.string().max(2000).nullable(),
      episodeTitle: z.string().max(300).nullable().optional(),
      year: z.number().int().nullable().optional(),
    })
    .optional(),
});
export type ProgressInput = z.infer<typeof progressSchema>;
